import React, { memo, useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import { useQuery } from '@apollo/react-hooks'
import { TeamMembers } from './components'
import { Helmet } from 'react-helmet'

import styles from './styles'
import { EEntityAboutSectionType, EEntity, TTeamProps } from 'models'
import {
	EntityAboutSection,
	EntityHeader /*Loading*/,
	Loading,
} from 'components'
import { StoreValue, TeamQuery } from 'store'
import { Informations, TeamFeed, EntityTeamLink } from './components'
import { truncateText, EnumUserRoles } from 'utils'

const useStyles = makeStyles(styles)

const TeamView: React.FunctionComponent<TTeamProps> = ({
	storeUserId,
	storeUserIg,
	storeCompanyPageViewAs,
	storeUser,
}) => {
	const {
		teamSlug,
		companySlug,
	}: { teamSlug: string; companySlug?: string } = useParams()
	const classes = useStyles()
	const { t } = useTranslation()
	const tabs: { [key: string]: string } = {
		index: t('The team'),
		posts: t('Team posts'),
	}

	const { data: teamData, loading: teamDataLoading }: any = useQuery(
		TeamQuery,
		{
			fetchPolicy: 'network-only',
			variables: {
				companySlug,
				slug: teamSlug,
			},
		},
	)

	const team = useMemo(() => (teamData && teamData.team) || null, [teamData])
	const [currentTabIndex, setCurrentTabIndex] = useState('index')
	const toggleTabIndex = useCallback(
		(index: string) => setCurrentTabIndex(index),
		[setCurrentTabIndex],
	)

	const checkUserRoles = useMemo(() => {
		// check the logged user's rights for current team
		if (!storeUser) return null
		else if (team) {
			// In order to edit a team, the logged user needs to be at least COMPANY_MANAGER
			const userRolesForCurrentTeam =
				storeUser.companyRoles &&
				storeUser.companyRoles
					.filter((item: any) => item.company.id === team.company.id)
					.shift()
			const checkRoles = userRolesForCurrentTeam
				? userRolesForCurrentTeam.roles.find((element: string) => {
						return element === EnumUserRoles.COMPANY_MANAGER
				  })
				: null
			return checkRoles
		}
	}, [storeUser, team])
	//TODO: CRITIC: I need to create a global function to check the edit rights/roles of the user
	const canEdit =
		storeUserIg ||
		(team && team.company && team.company.owner.id === storeUserId) ||
		checkUserRoles === EnumUserRoles.COMPANY_MANAGER

	const bCanEdit = storeCompanyPageViewAs === 'admin' && canEdit

	return teamDataLoading ? (
		<Loading size={48} />
	) : (
		<>
			<Helmet>
				<meta
					property="og:title"
					content={'CV30 - Make Mondays happy!'}
				/>
				<meta property="og:url" content={window.location.href} />
				<meta
					property="og:description"
					content={truncateText(
						(team && team.description?.replace(/(<([^>]+)>)/gi, "")) || '',
						100,
					)}
				/>
				{team && team.hasCover && (
					<meta property="og:image" content={team.hasCover} />
				)}
				{team && team.featuredVideo && (
					<meta property="og:video" content={team.featuredVideo} />
				)}
			</Helmet>
			<Grid
				className={classes.teamContainer}
				// style={teamContainerStyle}
				container={true}
				direction="row"
			>
				<EntityHeader
					toggle={toggleTabIndex}
					currentTabIndex={currentTabIndex}
					name={team && team.name}
					description={team && team.description}
					tabs={tabs}
					loading={false} // TODO add loading property from query
					team={team && team}
					canEdit={canEdit}
					bCanEdit={bCanEdit}
					type={EEntity.team}
				/>

				{currentTabIndex === 'index' && (
					<Grid
						className={classes.teamMainContent}
						container={true}
						direction="column"
					>
						{team && (
							<>
								<Informations
									id={team.id}
									name={team.name}
									femaleRatio={team.femaleRatio}
									maleRatio={team.maleRatio}
									department={team.department}
									noOfMembers={
										team.noOfMembers !== null
											? team.noOfMembers
											: ''
									}
									averageAge={team.averageAge}
									disabilityRatio={team.disabilityRatio}
									locations={team.locations}
									canEdit={bCanEdit}
									companyId={team.company && team.company.id}
									companyLocations={team.company.offices}
								/>
								<EntityTeamLink
									toggle={toggleTabIndex}
									teamName={team.name}
								/>
								<EntityAboutSection
									type={EEntityAboutSectionType.teamAbout}
									title={t('About-1')}
									emphasizedTitle={team.name + t(' team')}
									oneRowTitle={false}
									content={team.description}
									canEdit={bCanEdit}
									teamId={team.id || null}
								/>
								<TeamMembers
									teamId={team.id}
									members={team.members}
									bCanEdit={bCanEdit}
								/>
							</>
						)}
					</Grid>
				)}
				{currentTabIndex === 'posts' && (
					<TeamFeed
						companySlug={companySlug}
						teamId={team.id}
						teamName={team.name}
					/>
				)}
			</Grid>
		</>
	)
}

const Team = memo((props: TTeamProps) => (
	<StoreValue
		keys={[
			'user.id|storeUserId',
			'user._ig|storeUserIg',
			'user.uid|storeUserUid',
			'user|storeUser',
			'company.viewAs|viewAs',
			'guides.companyPageViewAs|storeCompanyPageViewAs',
		]}
	>
		<TeamView {...props} />
	</StoreValue>
))

export default Team
